import { ReactElement, useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import SectionHeader from 'shared/components/SectionHeader';
import { Utils } from 'shared/helpers';
import { PublicService } from 'services/PublicService';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonIcon from '@mui/icons-material/Person';
import EventIcon from '@mui/icons-material/Event';

export interface PolicyProps {
  _id: string;
  title: string;
  description: string;
  content: string;
  version: string;
  owner: string;
  status: string;
  organizationId: string;
  effectiveDate: any;
  publishedDate: any;
  updatedAt: any;
  acknowledgementRequired: boolean;
  policyMode: string;
  policyUrl: string;
  reviewDate: string;
  lastReviewedDate: any;
  reviewIntervalMonths: number;
  needsReview: boolean;
  isActive: boolean;
  deletedAt: any;
  deletedBy: any;
  deletionReason: any;
  createdAt: string;
  __v: number;
  frameworkIds: string[];
}

const PolicyList = (): ReactElement => {
  const [policyList, setPolicyList] = useState<PolicyProps[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const getPolicyList = (): void => {
    setIsLoading(true);
    PublicService.getPolicy()
      .then((res) => {
        const { data } = res.data;
        setPolicyList(data.policies);
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleToggle = (index: number): void => {
    setOpenIndex(openIndex === index ? null : index);
  };

  useEffect(() => {
    getPolicyList();
  }, []);
  console.log('policyList', policyList, isLoading);
  return (
    <Paper variant="outlined" className="p-4">
      <SectionHeader
        icon={DescriptionIcon}
        title={`Published Policies (${policyList.length})`}
        titleVariant="h3"
      />
      {policyList.length === 0 && isLoading && (
        <Box className="empty-content">
          <CircularProgress />
        </Box>
      )}
      {policyList.length > 0 && !isLoading && (
        <Box className="mt-3">
          {policyList.map((policy, index) => {
            const isOpen = openIndex === index;

            return (
              <Box className="policy-item" key={policy._id}>
                {/* HEADER */}
                <Box
                  className="policy-header"
                  onClick={() => handleToggle(index)}
                >
                  <Box>
                    <Typography className="policy-name">
                      {policy.title}
                    </Typography>
                    <Typography className="policy-version">
                      {policy.__v}
                    </Typography>
                  </Box>

                  <IconButton size="small">
                    <ExpandMoreIcon className={isOpen ? 'rotate' : ''} />
                  </IconButton>
                </Box>

                {/* CONTENT */}
                <Collapse in={isOpen}>
                  <Box className="policy-content">
                    {/* META */}
                    <Box className="policy-meta">
                      <Box className="meta-row">
                        <PersonIcon className="meta-icon" />
                        <Typography className="meta-label">
                          Policy Owner:
                        </Typography>
                        <Typography className="meta-value">
                          {policy.owner}
                        </Typography>
                      </Box>

                      <Box className="meta-row">
                        <EventIcon className="meta-icon" />
                        <Typography className="meta-label">
                          Effective Date:
                        </Typography>
                        <Typography className="meta-value">
                          {policy.effectiveDate}
                        </Typography>
                      </Box>
                    </Box>

                    {/* BODY */}
                    <Box className="policy-body">
                      <Typography className="policy-body-title">
                        Policy Content:
                      </Typography>

                      <Box className="policy-divider" />

                      <Typography className="policy-body-text word-break">
                        {policy.content.length > 1 ? `${policy.content}` : '-'}
                      </Typography>
                    </Box>
                  </Box>
                </Collapse>
              </Box>
            );
          })}
        </Box>
      )}

      {!isLoading && policyList.length === 0 && (
        <Box className="empty-content">
          <Typography>No approved policies found.</Typography>
        </Box>
      )}
    </Paper>
  );
};

export default PolicyList;
